<template>
  <!-- Floating Icon (Always visible when minimized) -->
  <div v-if="isActive && isMinimized" class="floating-call-icon" @click="restoreCall">
    <div class="icon-container">
      <div class="pulse-ring"></div>
      <i class="bi bi-telephone-fill"></i>
      <span class="call-timer-mini">{{ callTimer }}</span>
    </div>
    <div class="tooltip">Active Call: {{ activeCall.callerName }}</div>
  </div>

  <!-- Operator Overlay - Always on top -->
  <div v-if="isActive && !isMinimized" class="operator-overlay">
    <!-- Active Call Interface -->
    <div class="call-interface">
      <!-- Header with call info -->
      <div class="call-header">
        <div class="caller-info">
          <div class="emergency-indicator">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <h3>Emergency Call</h3>
          </div>
          <div class="caller-details">
            <div class="caller-main">
              <span class="caller-name">{{ activeCall?.callerName }}</span>
              <span class="call-type">{{
                formatEmergencyType(activeCall?.emergencyType)
              }}</span>
            </div>
            <div class="call-meta">
              <span class="call-timer">{{ callTimer }}</span>
              <div class="connection-status" :class="connectionHealth">
                <i class="bi bi-wifi"></i>
                {{ connectionHealth }}
              </div>
            </div>
          </div>
        </div>
        <div class="call-controls">
          <button @click="minimizeCall" class="btn-control" title="Minimize">
            <i class="bi bi-dash-lg"></i>
          </button>
          <button @click="endCall" class="btn-control btn-danger" title="End Call">
            <i class="bi bi-telephone-x"></i>
          </button>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="operator-grid">
        <!-- Video Feed Section -->
        <div class="video-section">
          <div class="video-container">
            <!-- Remote Video (Caller) -->
            <div class="video-feed remote-video">
              <video
                ref="remoteVideo"
                @loadedmetadata="onVideoLoaded"
                autoplay
                playsinline
              ></video>
              <div class="video-overlay">
                <div class="user-info">
                  <div class="user-badge">
                    <i class="bi bi-person-fill"></i>
                    <span class="user-name">{{ activeCall.callerName }}</span>
                    <span>{{ state.connectionStatus }}</span>
                  </div>
                  <div class="video-status" v-if="!remoteVideoActive">
                    <i class="bi bi-camera-video-off"></i>
                    <span>Video unavailable</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Local Video (Operator) -->
            <div class="video-feed local-video">
              <video ref="localVideo" autoplay muted playsinline></video>
              <div class="video-overlay">
                <div class="operator-badge">
                  <i class="bi bi-shield-check"></i>
                  <span>Operator</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Call Controls -->
          <div class="call-controls-bar">
            <button
              @click="toggleAudio"
              :class="{ active: isAudioEnabled }"
              class="control-btn"
              title="Toggle Microphone"
            >
              <i class="bi" :class="isAudioEnabled ? 'bi-mic-fill' : 'bi-mic-mute'"></i>
            </button>
            <button
              @click="toggleVideo"
              :class="{ active: isVideoEnabled }"
              class="control-btn"
              title="Toggle Camera"
            >
              <i
                class="bi"
                :class="
                  isVideoEnabled ? 'bi-camera-video-fill' : 'bi-camera-video-off-fill'
                "
              ></i>
            </button>
            <button
              @click="toggleSpeaker"
              :class="{ active: isSpeakerEnabled }"
              class="control-btn"
              title="Toggle Speaker"
            >
              <i
                class="bi"
                :class="isSpeakerEnabled ? 'bi-speaker-fill' : 'bi-speaker-mute'"
              ></i>
            </button>
            <button @click="endCall" class="control-btn btn-end-call" title="End Call">
              <i class="bi bi-telephone-x-fill"></i>
            </button>
          </div>
        </div>

        <!-- Operator Tools Sidebar -->
        <div class="tools-sidebar">
          <!-- Quick Actions -->
          <div class="tool-section">
            <div class="section-header">
              <i class="bi bi-lightning-fill"></i>
              <h4>Quick Actions</h4>
            </div>
            <div class="action-grid">
              <button @click="toggleMapModal" class="action-btn map-btn">
                <div class="action-icon">
                  <i class="bi bi-geo-alt-fill"></i>
                </div>
                <span class="action-text">View Location</span>
              </button>
              <button @click="checkIfCanShowModal" class="action-btn dispatch-btn">
                <div class="action-icon">
                  <i class="bi bi-send-fill"></i>
                </div>
                <span class="action-text">Dispatch Team</span>
              </button>
              <button @click="toggleNotesModal" class="action-btn notes-btn">
                <div class="action-icon">
                  <i class="bi bi-journal-text"></i>
                </div>
                <span class="action-text">Add Notes</span>
              </button>
            </div>

            <!-- Dispatched Teams -->
            <div
              v-if="dispatchedTeams && dispatchedTeams.length !== 0"
              class="dispatched-teams-section"
            >
              <div class="dispatched-header">
                <i class="bi bi-send-check"></i>
                <span>Dispatched Teams</span>
                <span class="badge-count">{{ dispatchedTeams.length }}</span>
              </div>
              <div class="badges-grid">
                <div
                  v-for="agency in dispatchedTeams"
                  :key="agency.agencyId"
                  class="agency-badge"
                  :class="getAgencyBadgeClass(agency.agencyName)"
                  :title="`${agency.agencyName} - ${
                    agency.agencyDescription || 'Dispatched team'
                  }`"
                >
                  <i :class="getAgencyIcon(agency.agencyName)"></i>
                  <span>{{ getAgencyShortName(agency.agencyName) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Caller Information -->
          <div class="tool-section">
            <div class="section-header">
              <i class="bi bi-person-badge-fill"></i>
              <h4>Caller Information</h4>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <label>Name</label>
                <div class="info-value">{{ activeCall.callerName }}</div>
              </div>
              <div class="info-item">
                <label>Location</label>
                <div class="info-value location-value">
                  <i class="bi bi-geo-alt"></i>
                  {{ activeCall.location }}
                </div>
              </div>
              <div class="info-item">
                <label>Emergency Type</label>
                <div style="cursor: pointer" @click="toggleChildModal" class="info-value">
                  <span
                    class="emergency-tag"
                    :class="getEmergencyClass(activeCall.emergencyType)"
                  >
                    {{ formatEmergencyType(activeCall.emergencyType) }}
                  </span>
                  <i class="bi bi-pencil"></i>
                </div>
              </div>
              <div v-if="activeCall.score > 0" class="info-item">
                <label>Fraud Probability</label>
                <div class="info-value">
                  <span class="risk-badge" :class="getRiskClass(activeCall.score)">
                    {{ activeCall.score }}%
                  </span>
                </div>
              </div>
              <div v-if="activeCall.warnings" class="info-item">
                <label>Red Flags</label>
                <div class="info-value warning-text">
                  <i class="bi bi-exclamation-triangle"></i>
                  {{ activeCall.warnings }}
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Responses -->
          <div class="tool-section">
            <div class="section-header">
              <i class="bi bi-chat-left-text-fill"></i>
              <h4>Quick Responses</h4>
            </div>
            <div class="responses-grid">
              <button
                v-for="response in quickResponses"
                :key="response.text"
                @click="sendQuickResponse(response.text)"
                class="response-btn"
                :title="response.text"
              >
                <i :class="response.icon"></i>
                <span>{{ response.text }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALS -->
    <MapModal
      ref="mapModalRef"
      :show="showMap"
      :id="activeCall.erId"
      :caller-location="activeCall.location"
      :caller-coordinates="activeCall.coordinates"
      @close="toggleMapModal"
    />

    <DispatchModal
      ref="dispatchModalRef"
      :show="showDispatch"
      :emergency-id="activeCall.erId"
      :emergency-type="activeCall.emergencyType"
      :caller-location="activeCall.location"
      @dispatch="handleDispatch"
      @close="toggleDispatchModal"
      @error="onDispatchError"
    />

    <CallerInfoModal
      v-if="showCallerInfo"
      :caller-data="activeCall"
      @close="hideCallerInfoModal"
    />

    <NotesModal
      :show="showNotes"
      :notes="activeCall?.notes"
      :id="activeCall.erId"
      @confirm="saveCallNotes"
      @close="toggleNotesModal"
    />

    <AlertModal
      @close="toggleAlertModal"
      :title="alertTitle"
      :type="alertType"
      :show="showAlert"
      :message="alertMessage"
    />

    <ChildModal :show="showChildModal" @close="toggleChildModal">
      <EditEmergencyType
        :id="activeCall?.erId"
        :emergncy-tpe="activeCall?.emergencyType"
        @confirmed="handleConfirmed"
        @closed="toggleChildModal"
      />
    </ChildModal>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, reactive } from "vue";
import MapModal from "../modals/MapModal.vue";
import DispatchModal from "../modals/DispatchModal.vue";
import CallerInfoModal from "../modals/CallerInfoModal.vue";
import NotesModal from "../modals/NotesModal.vue";
import signalRService from "@/services/signalr.service";
import webRTCService from "@/services/webrtc.service";
import AlertModal from "../modals/AlertModal.vue";
import ChildModal from "../modals/ChildModal.vue";
import EditEmergencyType from "../infos/EditEmergencyType.vue";
import emergencyTypeHelper from "@/utils/emergencyTypeHelper";
const dispatchModalRef = ref(null);
// Add quick responses array to your script
const quickResponses = [
  { text: "Help is on the way", icon: "bi bi-check-circle" },
  { text: "Stay on the line", icon: "bi bi-telephone" },
  { text: "Are you safe?", icon: "bi bi-shield-check" },
  { text: "Medical team dispatched", icon: "bi bi-ambulance" },
  { text: "Police are coming", icon: "bi bi-shield" },
  { text: "Fire department en route", icon: "bi bi-fire" },
];

// Add risk classification
const getRiskClass = (score) => {
  if (score < 30) return "low-risk";
  if (score < 70) return "medium-risk";
  return "high-risk";
};

// State
const isActive = ref(false);
const activeCall = ref(null);
const isMinimized = ref(false);

//Other data
const dispatchedTeams = ref([]);

// Video States
const localVideo = ref(null);
const remoteVideo = ref(null);
const isAudioEnabled = ref(true);
const isVideoEnabled = ref(true);
const isSpeakerEnabled = ref(true);
const remoteVideoActive = ref(false);
const connectionHealth = ref("good");

const showChildModal = ref(false);
const toggleChildModal = () => {
  showChildModal.value = !showChildModal.value;
};

//Alert Modal
const showAlert = ref(false);
const alertType = ref("error");
const alertMessage = ref("");
const alertTitle = ref("Error");
// Modal States
const showMap = ref(false);
const showDispatch = ref(false);
const showCallerInfo = ref(false);
const showNotes = ref(false);

// Call Timer
const callTimer = ref("00:00");
let callTimerInterval = null;
const startTime = ref(null);

//DispatchError
const onDispatchError = (msg) => {
  showAlert.value = true;
  alertTitle.value = "Dispatch Error";
  alertMessage.value = msg;
};

const toggleAlertModal = () => {
  showAlert.value = !showAlert.value;
};

const handleConfirmed = async (id) => {
  toggleChildModal();
  activeCall.value.emergencyType = id;
};

const getAgencyBadgeClass = (agencyName) => {
  const classMap = {
    POLICE: "police",
    FIRE: "fire",
    COASTGUARD: "coastguard",
    PERT: "pert",
    LEYECOV: "leyecov",
  };
  return classMap[agencyName] || "default";
};

const getAgencyShortName = (agencyName) => {
  const shortNames = {
    POLICE: "PNP",
    FIRE: "BFP",
    COASTGUARD: "PCG",
    PERT: "PERT",
    LEYECOV: "LEYECO V",
  };
  return shortNames[agencyName] || agencyName.substring(0, 8);
};

const getAgencyIcon = (agencyName) => {
  const iconMap = {
    POLICE: "bi-shield-check",
    FIRE: "bi-fire",
    COASTGUARD: "bi-life-preserver",
    PERT: "bi-heart-pulse",
    LEYECOV: "bi-lightning-charge",
  };
  return iconMap[agencyName] || "bi-building";
};

const startCall = async (callData) => {
  activeCall.value = {
    ...callData,
  };
  startTime.value = Date.now();
  isActive.value = true;
  isMinimized.value = false;
  await webRTCService.initialize();
  webRTCService.setTargetConnectionId(callData.callerConnectionId);

  webRTCService.setSignalRService(signalRService);
  setupWebRTCEventHandlers();

  await webRTCService.startCall();

  if (localVideo.value && webRTCService.localStream) {
    localVideo.value.srcObject = webRTCService.localStream;
  }

  // Send WebRTC offer to caller
  const offer = await webRTCService.createOffer();
  if (offer && callData.callerConnectionId) {
    await signalRService.sendOffer(callData.callerConnectionId, offer);
  }

  await signalRService.joinCallAsOperator(callData.callId);

  activeCall.value = callData;
};

// Reactive state
const state = reactive({
  connectionStatus: "connecting...", // connecting, connected, poor, disconnected, reconnecting
  connectionQuality: 1.0, // 0.0 to 1.0
  packetLoss: 0,
  roundTripTime: 0,
  timeSinceLastFrame: 0,
  isReconnecting: false,
  lastFrameTime: Date.now(),
  statsInterval: null,
  frameCheckInterval: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
});
const onVideoLoaded = () => {
  state.connectionStatus = "connected";
  startCallTimer();
};
// Setup WebRTC event handlers
const setupWebRTCEventHandlers = () => {
  webRTCService.onRemoteStream = (stream) => {
    console.log("Remote stream received");
    if (remoteVideo.value) {
      remoteVideo.value.srcObject = stream;
      state.connectionStatus = "connected";
      if (!callTimerInterval) startCallTimer();
    }
  };

  webRTCService.onConnectionStateChange = (rtcstate) => {
    console.log("state from local", rtcstate);
    if (rtcstate === "connected") {
      if (!callTimerInterval) startCallTimer();
    }
    state.connectionStatus = rtcstate;
  };

  webRTCService.onError = (context, error) => {
    console.error(`WebRTC error in ${context}:`, error);
  };
};

const onCheckSignalrStatusRequest = () => {
  const state = signalRService.getConnectionState();
  window.dispatchEvent(new CustomEvent("signalrConnectionState", { detail: state }));
};

const endCallLocal = async () => {
  await webRTCService.endCall();
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
    callTimerInterval = null;
  }
  // Reset state
  activeCall.value = null;
  dispatchedTeams.value = [];
  isActive.value = false;
  isMinimized.value = false;
};

const dispatchRefreshNeeded = () => {
  window.dispatchEvent(
    new CustomEvent("recentCallsRefreshNeeded", { detail: "refresh_recent_calls" })
  );
};

const endCall = async () => {
  await webRTCService.endCall();
  if (activeCall.value.callId) await signalRService.endCall(activeCall.value.callId);
  dispatchRefreshNeeded();
  // Clean up
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
    callTimerInterval = null;
  }
  dispatchedTeams.value = [];
  // Reset state
  activeCall.value = null;
  isActive.value = false;
  isMinimized.value = false;
};

const minimizeCall = () => {
  isMinimized.value = true;
};

const restoreCall = () => {
  isMinimized.value = false;
};
const startCallTimer = () => {
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
  }
  callTimerInterval = setInterval(() => {
    const seconds = Math.floor((Date.now() - startTime.value) / 1000); //callDuration.value;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    callTimer.value = `${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  }, 1000);
};

const mapModalRef = ref(null);
// Modal Methods
const toggleMapModal = () => {
  showMap.value = !showMap.value;
  if (showMap.value) {
    mapModalRef.value.clearResponderMarkers();
  }
};

const checkIfCanShowModal = () => {
  if (activeCall.value.emergencyType === 0) {
    alertMessage.value = "Please specify the emergency type first";
    alertTitle.value = "Emergency Type Required";
    toggleAlertModal();
  } else {
    toggleDispatchModal();
  }
};

const toggleDispatchModal = () => {
  showDispatch.value = !showDispatch.value;
  if (showDispatch.value) dispatchModalRef.value.getActiveAgencies();
};

const hideCallerInfoModal = () => (showCallerInfo.value = false);

const toggleNotesModal = () => {
  showNotes.value = !showNotes.value;
  if (showNotes.value) {
    const prevnotes = activeCall.value.notes || null;
    activeCall.value.notes = null;
    setTimeout(() => {
      activeCall.value.notes = prevnotes;
    }, 100);
  }
};

// WebRTC Methods
const toggleAudio = () => {
  isAudioEnabled.value = !isAudioEnabled.value;
  if (!localVideo.value || !localVideo.value.srcObject) {
    console.warn("No video element or stream found");
    return;
  }

  const stream = localVideo.value.srcObject;
  const audioTracks = stream.getAudioTracks();

  if (audioTracks.length === 0) {
    console.warn("No audio tracks found in stream");
    return;
  }

  // Toggle all audio tracks
  audioTracks.forEach((track) => {
    track.enabled = !track.enabled;
  });

  // Update UI state
  isAudioEnabled.value = audioTracks[0].enabled;

  console.log(`Microphone ${isAudioEnabled.value ? "unmuted" : "muted"}`);
};

const toggleVideo = () => {
  if (!localVideo.value || !localVideo.value.srcObject) {
    console.warn("No video element or stream found");
    return;
  }

  const stream = localVideo.value.srcObject;
  const videoTracks = stream.getVideoTracks();

  if (videoTracks.length === 0) {
    console.warn("No video tracks found in stream");
    return;
  }

  // Toggle all video tracks
  videoTracks.forEach((track) => {
    track.enabled = !track.enabled;
  });

  // Update UI state
  isVideoEnabled.value = videoTracks[0].enabled;

  console.log(`Video ${isVideoEnabled.value ? "enabled" : "disabled"}`);
};

const toggleSpeaker = () => {
  isSpeakerEnabled.value = !isSpeakerEnabled.value;
  // Implement speaker toggle logic
};

// Utility Methods
const getEmergencyClass = (emergencyType) => {
  return emergencyTypeHelper.getEmergencyClass(emergencyType);
};

const formatEmergencyType = (emergencyType) => {
  return emergencyTypeHelper.formatEmergencyType(emergencyType);
};

const sendQuickResponse = (message) => {
  // Send quick response to caller
  window.dispatchEvent(
    new CustomEvent("sendQuickResponse", {
      detail: { callId: activeCall.value.callId, message },
    })
  );
};

const handleDispatch = (dispatchData) => {
  toggleDispatchModal();
  dispatchedTeams.value = dispatchData;
  console.log("Dispatching team:", dispatchData);
  // Implement dispatch logic
};

const saveCallNotes = (notes) => {
  activeCall.value.notes = notes;
  toggleNotesModal();
};

const handleCallAccepted = (event) => {
  resetState();
  isActive.value = true;
  startCall(event.detail);
};

const handleCallEnded = (event) => {
  endCallLocal(event);
  dispatchRefreshNeeded();
};

const handleCallRejected = async (event) => {
  await signalRService.rejectEmergencyCall(event.detail);
  dispatchRefreshNeeded();
};
const resetState = () => {
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
  }
  state.connectionStatus = "connecting...";
  callTimer.value = "00:00";
  dispatchedTeams.value = [];
};

const handleNegotiationTimeout = (event) => {
  event;
  endCall();
};

//eslint-disable-next-line
defineExpose({
  resetState,
});

onMounted(async () => {
  window.addEventListener("callAccepted", handleCallAccepted);
  window.addEventListener("callEnded", handleCallEnded);
  window.addEventListener("callRejected", handleCallRejected);
  window.addEventListener("checkSignalrStatus", onCheckSignalrStatusRequest);
  window.addEventListener("webrtc:negotiation-timeout", handleNegotiationTimeout);
  if (signalRService.getConnectionState() !== "connected") await signalRService.connect();
});

onUnmounted(() => {
  window.removeEventListener("callAccepted", handleCallAccepted);
  window.removeEventListener("callEnded", handleCallEnded);
  window.removeEventListener("callRejected", handleCallRejected);
  window.removeEventListener("checkSignalrStatus", onCheckSignalrStatusRequest);
  window.removeEventListener("webrtc:negotiation-timeout", handleNegotiationTimeout);
  if (activeCall.value) endCall();
  if (callTimerInterval) {
    clearInterval(callTimerInterval);
  }
});
</script>

<style scoped>
/* Enhanced Base Styles */
.operator-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  font-family: "Segoe UI", system-ui, sans-serif;
}

.call-interface {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: transparent;
  overflow: hidden;
}

/* Enhanced Header */
.call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(255, 68, 68, 0.1);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.emergency-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ff4444;
}

.emergency-indicator i {
  font-size: 1.5rem;
  animation: pulse 2s infinite;
}

.emergency-indicator h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.caller-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  width: 100%;
}

.caller-main {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.caller-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.call-type {
  background: linear-gradient(135deg, #ff4444, #ff6b6b);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.call-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.call-timer {
  font-family: "JetBrains Mono", monospace;
  font-size: 1.1rem;
  font-weight: 600;
  color: #00d4aa;
  background: rgba(0, 212, 170, 0.1);
  padding: 0.3rem 0.8rem;
  border-radius: 10px;
}

.connection-status {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
}

.connection-status.good {
  background: rgba(0, 212, 170, 0.1);
  color: #00d4aa;
  border: 1px solid rgba(0, 212, 170, 0.3);
}

.call-controls {
  display: flex;
  gap: 0.5rem;
}

.btn-control {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-control:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.btn-control.btn-danger {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.btn-control.btn-danger:hover {
  background: rgba(220, 53, 69, 0.3);
}

/* Enhanced Grid Layout */
.operator-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  flex: 1;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow: hidden;
}

.video-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
}

/* Enhanced Video Container */
.video-container {
  position: relative;
  flex: 1;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  min-height: 0;
}

.video-feed {
  position: absolute;
  border-radius: 12px;
  overflow: hidden;
}

.remote-video {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.local-video {
  bottom: 1rem;
  right: 1rem;
  width: 180px;
  height: 135px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.user-badge,
.operator-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 0.8rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.video-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 68, 68, 0.8);
  padding: 0.4rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
}

/* Enhanced Call Controls */
.call-controls-bar {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.control-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.3rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-3px) scale(1.05);
}

.control-btn.active {
  background: #007bff;
  border-color: #007bff;
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.4);
}

.control-btn.btn-end-call {
  background: #dc3545;
  border-color: #dc3545;
}

.control-btn.btn-end-call:hover {
  background: #c82333;
  border-color: #c82333;
  box-shadow: 0 4px 16px rgba(220, 53, 69, 0.4);
}

/* Enhanced Tools Sidebar */
.tools-sidebar {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  overflow-y: auto;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
}

.tool-section {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #00d4aa;
}

.section-header i {
  font-size: 1.1rem;
}

.section-header h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Enhanced Action Grid */
.action-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 212, 170, 0.1);
  color: #00d4aa;
  font-size: 1.1rem;
}

.action-text {
  font-weight: 500;
  font-size: 0.9rem;
}

/* Enhanced Dispatched Teams */
.dispatched-teams-section {
  background: rgba(0, 212, 170, 0.05);
  border: 1px solid rgba(0, 212, 170, 0.2);
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1rem;
}

.dispatched-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #00d4aa;
  font-size: 0.85rem;
}

.badge-count {
  background: #00d4aa;
  color: #0c0c0c;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
}

.badges-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.agency-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  cursor: default;
}

.agency-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* Agency badge colors */
.agency-badge.police {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
}
.agency-badge.fire {
  background: linear-gradient(135deg, #fd7e14, #e55a00);
  color: white;
}
.agency-badge.coastguard {
  background: linear-gradient(135deg, #20c997, #17a2b8);
  color: white;
}
.agency-badge.pert {
  background: linear-gradient(135deg, #dc3545, #c82333);
  color: white;
}
.agency-badge.leyecov {
  background: linear-gradient(135deg, #6f42c1, #5a2d9c);
  color: white;
}

/* Enhanced Info Grid */
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  font-size: 0.8rem;
  color: #888;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: white;
}

.location-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.emergency-tag {
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.emergency-tag.medical {
  background: #dc3545;
  color: white;
}
.emergency-tag.fire {
  background: #fd7e14;
  color: white;
}
.emergency-tag.police {
  background: #0d6efd;
  color: white;
}
.emergency-tag.rescue {
  background: #198754;
  color: white;
}

.risk-badge {
  padding: 0.3rem 0.6rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
}

.risk-badge.low-risk {
  background: #198754;
  color: white;
}
.risk-badge.medium-risk {
  background: #ffc107;
  color: #000;
}
.risk-badge.high-risk {
  background: #dc3545;
  color: white;
}

.warning-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ffc107;
}

/* Enhanced Quick Responses */
.responses-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.response-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  text-align: left;
}

.response-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateX(4px);
}

.response-btn i {
  color: #00d4aa;
  font-size: 0.9rem;
  width: 16px;
}

/* Enhanced Floating Icon */
.floating-call-icon {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 10001;
  cursor: pointer;
}

.icon-container {
  position: relative;
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ff4444, #ff6b6b);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.8rem;
  box-shadow: 0 8px 32px rgba(255, 68, 68, 0.4);
  border: 3px solid white;
  transition: all 0.3s ease;
}

.floating-call-icon:hover .icon-container {
  transform: scale(1.1);
  box-shadow: 0 12px 40px rgba(255, 68, 68, 0.6);
}

.pulse-ring {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border: 2px solid #ff4444;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.call-timer-mini {
  position: absolute;
  bottom: -25px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 0.75rem;
  font-family: "JetBrains Mono", monospace;
  color: white;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  padding: 0.3rem 0.5rem;
  font-weight: 600;
}

.tooltip {
  position: absolute;
  bottom: 100%;
  right: 0;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.85rem;
  white-space: nowrap;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  pointer-events: none;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.floating-call-icon:hover .tooltip {
  opacity: 1;
  transform: translateY(0);
}

/* Animations */
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 1200px) {
  .operator-grid {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }

  .tools-sidebar {
    max-height: 400px;
  }
}

@media (max-width: 768px) {
  .operator-grid {
    padding: 1rem;
    gap: 1rem;
  }

  .call-header {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }

  .caller-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .local-video {
    width: 120px;
    height: 90px;
  }

  .control-btn {
    width: 48px;
    height: 48px;
    font-size: 1.1rem;
  }

  .floating-call-icon {
    bottom: 1rem;
    right: 1rem;
  }

  .icon-container {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
}

/* Scrollbar Styling */
.tools-sidebar::-webkit-scrollbar {
  width: 6px;
}

.tools-sidebar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.tools-sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.tools-sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
